"use client"

import { useState, useEffect } from "react"

import "./App.css"

import PlanetSelector from "./components/PlanetSelector"

import LawsSystem from "./components/LawsSystem"

import FriendsSystem from "./components/FriendsSystem"

import MissionControl from "./components/MissionControl"

import EvolutionLab from "./components/EvolutionLab"

import ColonyDashboard from "./components/ColonyDashboard"

import SpaceEventSystem from "./components/SpaceEventSystem"

import ColonyReports from "./components/ColonyReports"

import AboutPage from "./components/AboutPage"

import InstructionsPage from "./components/InstructionsPage"

import EmailSystem from "./components/EmailSystem"

import BuildingSystem from "./components/BuildingSystem"

import ResourcePanel from "./components/ResourcePanel"

function App() {
  const [activeTab, setActiveTab] = useState("planets")

  const [selectedPlanet, setSelectedPlanet] = useState(null)

  const [gameState, setGameState] = useState({
    user: {
      level: 1,

      evolutionPoints: 100,

      bestSurvivalRate: 85.2,

      intelligence: 25,

      engineering: 15,

      biology: 20,

      leadership: 10,
    },

    resources: {
      energy: 500,

      materials: 300,

      food: 400,

      water: 450,

      oxygen: 480,

      knowledge: 75,

      population: 1200,
    },

    buildings: [
      { id: "solar_1", name: "Solar Array", icon: "☀️", health: 85, level: 2, effects: { energyProduction: 25 } },

      { id: "farm_1", name: "Hydroponic Farm", icon: "🌱", health: 92, level: 1, effects: { foodProduction: 30 } },

      { id: "lab_1", name: "Research Lab", icon: "🧪", health: 78, level: 1, effects: { knowledgeProduction: 15 } },
    ],

    colonyHealth: 82,

    colonyMorale: 75,

    laws: [],

    friends: [],

    missions: [],

    evolutionData: {
      species: [],

      research: [],
    },
  })

  const [notifications, setNotifications] = useState([])

  // ENHANCED Resource management system with REAL consequences
  useEffect(() => {
    const interval = setInterval(() => {
      setGameState((prev) => {
        const newState = { ...prev }
        const newResources = { ...prev.resources }

        // Natural resource consumption
        newResources.food = Math.max(0, newResources.food - 3)
        newResources.energy = Math.max(0, newResources.energy - 5)
        newResources.water = Math.max(0, newResources.water - 2)
        newResources.oxygen = Math.max(0, newResources.oxygen - 4)

        // Building production
        prev.buildings.forEach((building) => {
          const efficiency = building.health / 100
          if (building.effects) {
            Object.entries(building.effects).forEach(([effect, value]) => {
              if (effect === "energyProduction") {
                newResources.energy += Math.floor(value * efficiency)
              } else if (effect === "foodProduction") {
                newResources.food += Math.floor(value * efficiency)
              } else if (effect === "knowledgeProduction") {
                newResources.knowledge += Math.floor(value * efficiency)
              } else if (effect === "waterProduction") {
                newResources.water += Math.floor(value * efficiency)
              } else if (effect === "oxygenProduction") {
                newResources.oxygen += Math.floor(value * efficiency)
              }
            })
          }
        })

        // Cap resources at 1000
        Object.keys(newResources).forEach((key) => {
          if (key !== "population") {
            newResources[key] = Math.min(1000, Math.max(0, newResources[key]))
          }
        })

        // DEATH SYSTEM - People actually die when resources are critically low
        let deaths = 0
        const deathReasons = []

        if (newResources.food < 100) {
          const foodDeaths = Math.floor(Math.random() * 25) + 10 // 10-35 deaths from starvation
          deaths += foodDeaths
          deathReasons.push(`${foodDeaths} died from starvation`)
        }

        if (newResources.water < 80) {
          const waterDeaths = Math.floor(Math.random() * 30) + 15 // 15-45 deaths from dehydration
          deaths += waterDeaths
          deathReasons.push(`${waterDeaths} died from dehydration`)
        }

        if (newResources.oxygen < 100) {
          const oxygenDeaths = Math.floor(Math.random() * 35) + 20 // 20-55 deaths from suffocation
          deaths += oxygenDeaths
          deathReasons.push(`${oxygenDeaths} suffocated to death`)
        }

        if (newResources.energy < 50) {
          const energyDeaths = Math.floor(Math.random() * 20) + 5 // 5-25 deaths from life support failure
          deaths += energyDeaths
          deathReasons.push(`${energyDeaths} died from life support failure`)
        }

        // Apply deaths
        if (deaths > 0) {
          newResources.population = Math.max(0, newResources.population - deaths)
          addNotification({
            type: "error",
            title: "💀 MASS CASUALTIES",
            description: `${deaths} colonists have died! ${deathReasons.join(", ")}. Population: ${Math.floor(newResources.population)}`,
          })
        }

        // REALISTIC Health and Morale calculation based on actual conditions
        let healthScore = 100
        let moraleScore = 100

        // Health penalties for resource shortages
        if (newResources.food < 200) {
          const penalty = Math.max(5, (200 - newResources.food) / 4) // Up to 50 point penalty
          healthScore -= penalty
        }
        if (newResources.water < 200) {
          const penalty = Math.max(5, (200 - newResources.water) / 3) // Up to 67 point penalty
          healthScore -= penalty
        }
        if (newResources.oxygen < 300) {
          const penalty = Math.max(10, (300 - newResources.oxygen) / 3) // Up to 100 point penalty
          healthScore -= penalty
        }
        if (newResources.energy < 200) {
          const penalty = Math.max(5, (200 - newResources.energy) / 5) // Up to 40 point penalty
          healthScore -= penalty
        }

        // Morale penalties for resource shortages and deaths
        if (newResources.food < 300) {
          const penalty = Math.max(10, (300 - newResources.food) / 3) // Up to 100 point penalty
          moraleScore -= penalty
        }
        if (newResources.water < 300) {
          const penalty = Math.max(15, (300 - newResources.water) / 2) // Up to 150 point penalty
          moraleScore -= penalty
        }
        if (newResources.energy < 300) {
          const penalty = Math.max(5, (300 - newResources.energy) / 6) // Up to 50 point penalty
          moraleScore -= penalty
        }

        // Death penalty to morale
        if (deaths > 0) {
          moraleScore -= deaths * 2 // Each death reduces morale by 2 points
        }

        // Population loss penalty
        const populationLossRatio = newResources.population / 1200 // Original population was 1200
        if (populationLossRatio < 0.8) {
          moraleScore -= (0.8 - populationLossRatio) * 100 // Up to 80 point penalty
        }

        // Law penalties (if laws are oppressive)
        const oppressiveLaws = prev.laws.filter(
          (law) =>
            law.title.toLowerCase().includes("ban") ||
            law.title.toLowerCase().includes("no") ||
            law.title.toLowerCase().includes("forbidden") ||
            law.title.toLowerCase().includes("prohibited"),
        )
        moraleScore -= oppressiveLaws.length * 15 // Each oppressive law reduces morale by 15

        // Apply calculated values
        const newColonyHealth = Math.max(0, Math.min(100, healthScore))
        const newColonyMorale = Math.max(0, Math.min(100, moraleScore))

        // Update survival rate based on actual conditions
        const resourceAverage =
          ((newResources.food + newResources.water + newResources.energy + newResources.oxygen) / 4000) * 100
        const populationHealth = (newResources.population / 1200) * 100
        const overallSurvival = (resourceAverage + populationHealth + newColonyHealth + newColonyMorale) / 4

        return {
          ...newState,
          resources: newResources,
          colonyHealth: newColonyHealth,
          colonyMorale: newColonyMorale,
          user: {
            ...newState.user,
            bestSurvivalRate: Math.max(newState.user.bestSurvivalRate, overallSurvival),
          },
        }
      })
    }, 8000) // Every 8 seconds

    return () => clearInterval(interval)
  }, [])

  const addNotification = (notification) => {
    const id = Date.now() + Math.random()

    setNotifications((prev) => [...prev, { ...notification, id }])

    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id))
    }, 5000)
  }

  const removeNotification = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id))
  }

  const handlePlanetSelect = (planet) => {
    setSelectedPlanet(planet)

    addNotification({
      type: "success",

      title: "Planet Selected",

      description: `${planet.name} chosen for colonization. Prepare for the journey!`,
    })
  }

  const renderCurrentTab = () => {
    if (selectedPlanet && activeTab === "planets") {
      return (
        <ColonyDashboard
          planet={selectedPlanet}
          gameState={gameState}
          setGameState={setGameState}
          onBack={() => setSelectedPlanet(null)}
          addNotification={addNotification}
        />
      )
    }

    switch (activeTab) {
      case "planets":
        return (
          <PlanetSelector
            onPlanetSelect={handlePlanetSelect}
            userLevel={gameState.user.level}
            addNotification={addNotification}
          />
        )

      case "laws":
        return <LawsSystem gameState={gameState} setGameState={setGameState} addNotification={addNotification} />

      case "friends":
        return <FriendsSystem gameState={gameState} setGameState={setGameState} addNotification={addNotification} />

      case "missions":
        return <MissionControl gameState={gameState} setGameState={setGameState} addNotification={addNotification} />

      case "evolution":
        return <EvolutionLab gameState={gameState} setGameState={setGameState} addNotification={addNotification} />

      case "buildings":
        return <BuildingSystem gameState={gameState} setGameState={setGameState} addNotification={addNotification} />

      case "reports":
        return <ColonyReports gameState={gameState} />

      case "about":
        return <AboutPage />

      case "instructions":
        return <InstructionsPage />

      default:
        return <PlanetSelector onPlanetSelect={handlePlanetSelect} userLevel={gameState.user.level} />
    }
  }

  return (
    <div className="bioforge-app">
      <div className="space-background">
        <div className="stars-layer"></div>

        <div className="planets-layer"></div>
      </div>

      {/* Corner Navigation */}

      <div className="corner-nav">
        <button className={`corner-btn ${activeTab === "about" ? "active" : ""}`} onClick={() => setActiveTab("about")}>
          ℹ️ About
        </button>

        <button
          className={`corner-btn ${activeTab === "instructions" ? "active" : ""}`}
          onClick={() => setActiveTab("instructions")}
        >
          📖 Guide
        </button>
      </div>

      {/* Email System - Bottom Right */}

      <EmailSystem gameState={gameState} addNotification={addNotification} />

      {/* Resource Panel - Next to Email */}

      <ResourcePanel gameState={gameState} />

      {/* Top Stats Bar */}

      <div className="top-stats-bar">
        <div className="stat-item">
          <span className="stat-icon">⚡</span>

          <span className="stat-value">{gameState.user.evolutionPoints}</span>

          <span className="stat-label">EVOLUTION POINTS</span>
        </div>

        <div className="stat-item survival-stat">
          <span className="stat-label">BEST SURVIVAL RATE</span>

          <span className="stat-value">{Math.floor(gameState.user.bestSurvivalRate)}%</span>
        </div>

        <div className="stat-item">
          <span className="stat-icon">🏆</span>

          <span className="stat-value">{gameState.user.level}</span>

          <span className="stat-label">LEVEL</span>
        </div>
      </div>

      {/* Main Title */}

      <div className="main-title">
        <h1 className="bioforge-title">BIOFORGE</h1>

        <p className="subtitle">EVOLUTIONARY SPACE COLONIZATION</p>

        <p className="description">
          Engineer life itself across the cosmos. Create, evolve, and colonize the galaxy with custom-designed
          organisms.
        </p>
      </div>

      {/* Navigation - ALL UNLOCKED */}

      <div className="game-nav">
        <div className={`nav-tab ${activeTab === "planets" ? "active" : ""}`} onClick={() => setActiveTab("planets")}>
          <span className="tab-icon">🌍</span>

          <span className="tab-name">PLANETS</span>
        </div>

        <div className={`nav-tab ${activeTab === "laws" ? "active" : ""}`} onClick={() => setActiveTab("laws")}>
          <span className="tab-icon">⚖️</span>

          <span className="tab-name">LAWS</span>
        </div>

        <div className={`nav-tab ${activeTab === "friends" ? "active" : ""}`} onClick={() => setActiveTab("friends")}>
          <span className="tab-icon">🚀</span>

          <span className="tab-name">FRIENDS</span>
        </div>

        <div className={`nav-tab ${activeTab === "missions" ? "active" : ""}`} onClick={() => setActiveTab("missions")}>
          <span className="tab-icon">🎯</span>

          <span className="tab-name">MISSIONS</span>
        </div>

        <div
          className={`nav-tab ${activeTab === "evolution" ? "active" : ""}`}
          onClick={() => setActiveTab("evolution")}
        >
          <span className="tab-icon">🧬</span>

          <span className="tab-name">EVOLUTION</span>
        </div>

        <div
          className={`nav-tab ${activeTab === "buildings" ? "active" : ""}`}
          onClick={() => setActiveTab("buildings")}
        >
          <span className="tab-icon">🏗️</span>

          <span className="tab-name">BUILDINGS</span>
        </div>

        <div className={`nav-tab ${activeTab === "reports" ? "active" : ""}`} onClick={() => setActiveTab("reports")}>
          <span className="tab-icon">📊</span>

          <span className="tab-name">REPORTS</span>
        </div>
      </div>

      {/* Game Content */}

      <div className="game-content">{renderCurrentTab()}</div>

      {/* Space Events System */}

      <SpaceEventSystem gameState={gameState} setGameState={setGameState} addNotification={addNotification} />

      {/* Notifications */}

      <div className="space-notifications">
        {notifications.map((notification) => (
          <div key={notification.id} className={`space-notification ${notification.type}`}>
            <div className="notification-header">
              <span className="notification-icon">
                {notification.type === "success"
                  ? "✅"
                  : notification.type === "warning"
                    ? "⚠️"
                    : notification.type === "error"
                      ? "❌"
                      : "ℹ️"}
              </span>

              <span className="notification-title">{notification.title}</span>

              <button className="notification-close" onClick={() => removeNotification(notification.id)}>
                ×
              </button>
            </div>

            <div className="notification-description">{notification.description}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default App
